import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, CheckCircle, Loader, Camera } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import usePageMeta from "./hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function IgreenCadastro() {
  usePageMeta("Pavan e Parazzi iGreen", "pp");
  
  const navigate = useNavigate();
  const [tipoPessoa, setTipoPessoa] = useState("CPF");
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    nome_completo: "",
    nome_empresa_responsavel: "",
    email: "",
    whatsapp: "",
    cnpj: "",
    documento_frente: "",
    documento_verso: "",
    conta_luz: "",
    contrato_social: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTipoPessoaChange = (tipo) => {
    setTipoPessoa(tipo);
    // Limpar campos ao trocar
    setFormData({
      nome_completo: "",
      nome_empresa_responsavel: "",
      email: "",
      whatsapp: "",
      cnpj: "",
      documento_frente: "",
      documento_verso: "",
      conta_luz: "",
      contrato_social: ""
    });
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Apenas arquivos JPG, PNG ou PDF são permitidos');
      return;
    }

    // Validar tamanho (5MB máximo)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, [fieldName]: reader.result }));
        toast.success('Arquivo carregado com sucesso');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erro ao carregar arquivo');
    }
  };

  const validateForm = () => {
    if (tipoPessoa === "CPF") {
      if (!formData.nome_completo || !formData.email || !formData.whatsapp) {
        toast.error('Preencha todos os campos obrigatórios');
        return false;
      }
    } else {
      if (!formData.nome_empresa_responsavel || !formData.email || !formData.whatsapp || !formData.cnpj) {
        toast.error('Preencha todos os campos obrigatórios');
        return false;
      }
    }

    if (!formData.documento_frente || !formData.documento_verso || !formData.conta_luz) {
      toast.error('Envie todos os documentos obrigatórios');
      return false;
    }

    if (tipoPessoa === "CNPJ" && !formData.contrato_social) {
      toast.error('Contrato Social é obrigatório para Pessoa Jurídica');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        tipo_pessoa: tipoPessoa,
        nome: tipoPessoa === "CPF" ? formData.nome_completo : formData.nome_empresa_responsavel,
        email: formData.email,
        whatsapp: formData.whatsapp,
        cnpj: tipoPessoa === "CNPJ" ? formData.cnpj : null,
        documento_frente: formData.documento_frente,
        documento_verso: formData.documento_verso,
        conta_luz: formData.conta_luz,
        contrato_social: tipoPessoa === "CNPJ" ? formData.contrato_social : null,
        origem: 'igreencadastro'
      };

      await axios.post(`${API}/cadastros-igreen-publico`, payload);
      
      setShowSuccess(true);
      
      // Limpar formulário
      setFormData({
        nome_completo: "",
        nome_empresa_responsavel: "",
        email: "",
        whatsapp: "",
        cnpj: "",
        documento_frente: "",
        documento_verso: "",
        conta_luz: "",
        contrato_social: ""
      });

      // Redirecionar após 10 segundos
      setTimeout(() => {
        navigate('/');
      }, 10000);

    } catch (error) {
      console.error('Erro ao enviar cadastro:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar cadastro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_finance-dash-68/artifacts/e2onqydh_Logo%20igreen%202%20-%20sem%20fundo.png" 
            alt="Igreen Energy" 
            className="h-16 w-auto mx-auto"
          />
        </div>
      </div>

      {/* Formulário */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {!showSuccess ? (
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            {/* Título */}
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-[#006437] mb-3">
                Cadastro Igreen Energy
              </h1>
              <p className="text-gray-600 text-lg">
                Preencha seus dados e envie seus documentos para darmos andamento à sua economia com energia solar.
              </p>
            </div>

            {/* Seletor de Tipo de Pessoa */}
            <div className="mb-8">
              <p className="text-gray-700 font-semibold mb-4 text-center">
                Você está se cadastrando como:
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  type="button"
                  onClick={() => handleTipoPessoaChange("CPF")}
                  className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
                    tipoPessoa === "CPF"
                      ? 'bg-[#006437] text-white shadow-lg scale-105'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Pessoa Física (CPF)
                </button>
                <button
                  type="button"
                  onClick={() => handleTipoPessoaChange("CNPJ")}
                  className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
                    tipoPessoa === "CNPJ"
                      ? 'bg-[#006437] text-white shadow-lg scale-105'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Pessoa Jurídica (CNPJ)
                </button>
              </div>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Campos para CPF */}
              {tipoPessoa === "CPF" && (
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Nome completo *
                  </label>
                  <input
                    type="text"
                    name="nome_completo"
                    value={formData.nome_completo}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#006437] focus:outline-none transition-colors"
                    placeholder="Digite seu nome completo"
                    required
                  />
                </div>
              )}

              {/* Campos para CNPJ */}
              {tipoPessoa === "CNPJ" && (
                <>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Nome da Empresa / Responsável *
                    </label>
                    <input
                      type="text"
                      name="nome_empresa_responsavel"
                      value={formData.nome_empresa_responsavel}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#006437] focus:outline-none transition-colors"
                      placeholder="Digite o nome da empresa ou responsável"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      CNPJ *
                    </label>
                    <input
                      type="text"
                      name="cnpj"
                      value={formData.cnpj}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#006437] focus:outline-none transition-colors"
                      placeholder="00.000.000/0000-00"
                      required
                    />
                  </div>
                </>
              )}

              {/* Email */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  E-mail *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#006437] focus:outline-none transition-colors"
                  placeholder="seu@email.com"
                  required
                />
              </div>

              {/* WhatsApp */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  WhatsApp *
                </label>
                <input
                  type="tel"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#006437] focus:outline-none transition-colors"
                  placeholder="(00) 00000-0000"
                  required
                />
              </div>

              {/* Documentos */}
              <div className="space-y-4 pt-4">
                <h3 className="text-xl font-bold text-[#006437] mb-4">
                  Documentos *
                </h3>

                {/* Documento Frente */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {tipoPessoa === "CPF" 
                      ? "Upload RG ou CPF - Frente" 
                      : "Upload Frente CPF/CNH - Responsável (QSA)"}
                  </label>
                  
                  {/* Botão de Upload Normal */}
                  <div className="relative mb-3">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => handleFileUpload(e, 'documento_frente')}
                      className="hidden"
                      id="documento_frente"
                    />
                    <label
                      htmlFor="documento_frente"
                      className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#006437] cursor-pointer transition-colors bg-gray-50 hover:bg-green-50"
                    >
                      {formData.documento_frente ? (
                        <div className="flex items-center text-[#006437]">
                          <CheckCircle className="w-6 h-6 mr-2" />
                          <span className="font-medium">Arquivo carregado</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-500">
                          <Upload className="w-6 h-6 mr-2" />
                          <span>Clique para enviar (JPG, PNG ou PDF)</span>
                        </div>
                      )}
                    </label>
                  </div>

                  {/* Botão de Câmera - Somente Mobile */}
                  <div className="relative md:hidden">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handleFileUpload(e, 'documento_frente')}
                      className="hidden"
                      id="camera_documento_frente"
                    />
                    <label
                      htmlFor="camera_documento_frente"
                      className="flex items-center justify-center w-full px-4 py-3 border-2 border-[#006437] rounded-xl bg-[#006437] text-white cursor-pointer transition-all hover:bg-[#00994B]"
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      <span className="font-medium">📸 Tirar foto</span>
                    </label>
                  </div>
                </div>

                {/* Documento Verso */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {tipoPessoa === "CPF" 
                      ? "Upload RG ou CPF - Verso" 
                      : "Upload Verso CPF/CNH - Responsável (QSA)"}
                  </label>
                  
                  {/* Botão de Upload Normal */}
                  <div className="relative mb-3">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => handleFileUpload(e, 'documento_verso')}
                      className="hidden"
                      id="documento_verso"
                    />
                    <label
                      htmlFor="documento_verso"
                      className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#006437] cursor-pointer transition-colors bg-gray-50 hover:bg-green-50"
                    >
                      {formData.documento_verso ? (
                        <div className="flex items-center text-[#006437]">
                          <CheckCircle className="w-6 h-6 mr-2" />
                          <span className="font-medium">Arquivo carregado</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-500">
                          <Upload className="w-6 h-6 mr-2" />
                          <span>Clique para enviar (JPG, PNG ou PDF)</span>
                        </div>
                      )}
                    </label>
                  </div>

                  {/* Botão de Câmera - Somente Mobile */}
                  <div className="relative md:hidden">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handleFileUpload(e, 'documento_verso')}
                      className="hidden"
                      id="camera_documento_verso"
                    />
                    <label
                      htmlFor="camera_documento_verso"
                      className="flex items-center justify-center w-full px-4 py-3 border-2 border-[#006437] rounded-xl bg-[#006437] text-white cursor-pointer transition-all hover:bg-[#00994B]"
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      <span className="font-medium">📸 Tirar foto</span>
                    </label>
                  </div>
                </div>

                {/* Conta de Luz */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {tipoPessoa === "CPF" 
                      ? "Upload Conta de Luz" 
                      : "Upload Última Conta de Energia"}
                  </label>
                  
                  {/* Botão de Upload Normal */}
                  <div className="relative mb-3">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => handleFileUpload(e, 'conta_luz')}
                      className="hidden"
                      id="conta_luz"
                    />
                    <label
                      htmlFor="conta_luz"
                      className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#006437] cursor-pointer transition-colors bg-gray-50 hover:bg-green-50"
                    >
                      {formData.conta_luz ? (
                        <div className="flex items-center text-[#006437]">
                          <CheckCircle className="w-6 h-6 mr-2" />
                          <span className="font-medium">Arquivo carregado</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-500">
                          <Upload className="w-6 h-6 mr-2" />
                          <span>Clique para enviar (JPG, PNG ou PDF)</span>
                        </div>
                      )}
                    </label>
                  </div>

                  {/* Botão de Câmera - Somente Mobile */}
                  <div className="relative md:hidden">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handleFileUpload(e, 'conta_luz')}
                      className="hidden"
                      id="camera_conta_luz"
                    />
                    <label
                      htmlFor="camera_conta_luz"
                      className="flex items-center justify-center w-full px-4 py-3 border-2 border-[#006437] rounded-xl bg-[#006437] text-white cursor-pointer transition-all hover:bg-[#00994B]"
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      <span className="font-medium">📸 Tirar foto</span>
                    </label>
                  </div>
                </div>

                {/* Contrato Social - apenas para PJ */}
                {tipoPessoa === "CNPJ" && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Upload Contrato Social da Empresa
                    </label>
                    
                    {/* Botão de Upload Normal */}
                    <div className="relative mb-3">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => handleFileUpload(e, 'contrato_social')}
                        className="hidden"
                        id="contrato_social"
                      />
                      <label
                        htmlFor="contrato_social"
                        className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#006437] cursor-pointer transition-colors bg-gray-50 hover:bg-green-50"
                      >
                        {formData.contrato_social ? (
                          <div className="flex items-center text-[#006437]">
                            <CheckCircle className="w-6 h-6 mr-2" />
                            <span className="font-medium">Arquivo carregado</span>
                          </div>
                        ) : (
                          <div className="flex items-center text-gray-500">
                            <Upload className="w-6 h-6 mr-2" />
                            <span>Clique para enviar (JPG, PNG ou PDF)</span>
                          </div>
                        )}
                      </label>
                    </div>

                    {/* Botão de Câmera - Somente Mobile */}
                    <div className="relative md:hidden">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleFileUpload(e, 'contrato_social')}
                        className="hidden"
                        id="camera_contrato_social"
                      />
                      <label
                        htmlFor="camera_contrato_social"
                        className="flex items-center justify-center w-full px-4 py-3 border-2 border-[#006437] rounded-xl bg-[#006437] text-white cursor-pointer transition-all hover:bg-[#00994B]"
                      >
                        <Camera className="w-5 h-5 mr-2" />
                        <span className="font-medium">📸 Tirar foto</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Botão de Envio */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#006437] hover:bg-[#00994B] text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader className="animate-spin mr-2" />
                      Enviando...
                    </>
                  ) : (
                    'Enviar Cadastro'
                  )}
                </button>
              </div>
            </form>
          </div>
        ) : (
          /* Mensagem de Sucesso */
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 text-center animate-fade-in">
            <div className="mb-6">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-[#006437]" />
              </div>
              <h2 className="text-3xl font-bold text-[#006437] mb-4">
                🎉 Parabéns pela sua iniciativa de economia!
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-4">
                A equipe da <strong>Pavan e Parazzi</strong> entrará em contato em breve para dar continuidade ao seu cadastro junto à <strong>Igreen Energy</strong>.
              </p>
            </div>
            <div className="mt-8">
              <p className="text-gray-500 text-sm">
                Você será redirecionado para a página inicial em instantes...
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-[#1A472A] text-white py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Logo e Info */}
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_finance-dash-68/artifacts/e2onqydh_Logo%20igreen%202%20-%20sem%20fundo.png" 
                alt="Igreen Energy"
                className="h-16 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-white/80 text-sm">
                Igreen Energy – Energia solar compartilhada de forma simples, sustentável e segura.
              </p>
              <p className="text-white/80 text-sm mt-2">
                <strong>Parceria oficial: Pavan & Parazzi</strong>
              </p>
            </div>

            {/* Links Rápidos */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Links Úteis</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-white/80 hover:text-white transition-colors">
                    Política de Privacidade
                  </a>
                </li>
                <li>
                  <a href="#" className="text-white/80 hover:text-white transition-colors">
                    Termos de Uso
                  </a>
                </li>
                <li>
                  <a href="#" className="text-white/80 hover:text-white transition-colors">
                    Contato
                  </a>
                </li>
              </ul>
            </div>

            {/* Grupo Vibra */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Grupo Vibra</h3>
              <p className="text-white/80 text-sm">
                A Igreen Energy é do <strong>Grupo Vibra</strong>, uma das maiores empresas de energia do Brasil, garantindo confiabilidade e excelência em todos os serviços.
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="text-white/70 text-sm">
              © 2025 Pavan e Parazzi – Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default IgreenCadastro;
